import { AccountMergingPrimaryScreen } from "./AccountMergingPrimaryScreen";
import { AccountMergingSecondaryScreen } from "./AccountMergingSecondaryScreen";
import type { AccountMergingResponse } from "../../application/services/accountMergingService";
import { BasicDialog } from "./BasicDialog";
import type { AccountMergingFlowStep } from "../hooks/useAccountMerging";
import { Loader2 } from "lucide-react";
import { MergingConfirmDialog } from "./MergingConfirmDialog";
import { MergingOTPDialog } from "./MergingOTPDialog";
import { MergingPreVerifyDialog } from "./MergingPreVerifyDialog";

interface AccountMergingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: AccountMergingResponse | null;
  isLoading: boolean;
  flowStep: AccountMergingFlowStep;

  primaryEmail: string | null;
  setPrimaryEmail: (email: string) => void;
  secondaryActions: Record<string, string>;
  setSecondaryAction: (email: string, action: string) => void;
  transferInputs: Record<string, string>;
  setTransferInput: (email: string, value: string) => void;
  transferErrors: Record<string, string>;
  verifiedTransfers: Record<string, { phoneNumber: string; otpId: number }>;

  goBackToPrimary: () => void;
  proceedToSecondary: () => void;
  otpState: { isOpen: boolean; isPreOpen: boolean; type: "self" | "re-assign" | null; phoneNumber: string; email?: string };
  isOtpLoading: boolean;
  isVerifyLoading: boolean;
  isResendLoading: boolean;
  otpMessage: { text: string; type: string };
  sendInitialSelfOtp: () => void;
  requestOtp: (phoneNumber: string, type: "re-assign", email: string) => void;
  verifyOtp: (otp: string) => void;
  resendOtp: () => void;
  closeOtp: () => void;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  submitMerging: () => void;
  isConfirmOpen: boolean;
  setIsConfirmOpen: (open: boolean) => void;
  getAvatarColor: (index: number) => string;
  handleLogout: () => void;

  resendCountdown: number;
}

export function AccountMergingDialog({
  open,
  data,
  isLoading,
  flowStep,
  primaryEmail,
  setPrimaryEmail,
  secondaryActions,
  setSecondaryAction,
  transferInputs,
  setTransferInput,
  transferErrors,
  verifiedTransfers,
  goBackToPrimary,
  proceedToSecondary,
  otpState,
  isOtpLoading,
  isVerifyLoading,
  isResendLoading,
  otpMessage,
  sendInitialSelfOtp,
  requestOtp,
  verifyOtp,
  resendOtp,
  closeOtp,
  isSubmitting,
  isSubmitDisabled,
  submitMerging,
  isConfirmOpen,
  setIsConfirmOpen,
  getAvatarColor,
  handleLogout,
  resendCountdown,
}: AccountMergingDialogProps) {


  return (
    <BasicDialog open={open}>
      {isLoading && (
        <div className="flex justify-center items-center py-10 text-gray-500">
          <Loader2 className="animate-spin mr-2" /> Loading accounts...
        </div>
      )}

      {!isLoading && data && (
        <>
          {flowStep === "PRIMARY" && (
            <AccountMergingPrimaryScreen
              data={data}
              isLoading={isLoading}
              primaryEmail={primaryEmail}
              onSelect={setPrimaryEmail}
              onContinue={proceedToSecondary}
              getAvatarColor={getAvatarColor}
              handleLogout={handleLogout}
            />
          )}

          {flowStep === "SECONDARY" && primaryEmail && (
            <AccountMergingSecondaryScreen
              phoneNumber={data.customerPhone}
              accountsData={data.customerAccountsData}
              primaryEmail={primaryEmail}
              onBack={goBackToPrimary} 
              secondaryActions={secondaryActions}
              onActionSelect={setSecondaryAction}
              transferInputs={transferInputs}
              onTransferInputChange={setTransferInput}
              transferErrors={transferErrors}
              verifiedTransfers={verifiedTransfers}
              onRequestOtp={requestOtp}
              isOtpLoading={isOtpLoading}
              isSubmitting={isSubmitting}
              onSubmit={() => setIsConfirmOpen(true)}
              isSubmitDisabled={isSubmitDisabled}
              getAvatarColor={getAvatarColor}
            />
          )}
        </>
      )}

      {otpState.isPreOpen && (
        <MergingPreVerifyDialog
          phoneNumber={otpState.phoneNumber}
          isOtpLoading={isOtpLoading}
          onSendOtp={sendInitialSelfOtp}
          handleLogout={handleLogout}
        />
      )}

      {otpState.isOpen && (
        <MergingOTPDialog 
          phoneNumber={otpState.phoneNumber}
          blocking={otpState.type === "self"}
          isVerifyLoading={isVerifyLoading}
          isResendLoading={isResendLoading}
          otpMessage={otpMessage}
          onVerify={verifyOtp}
          onResend={resendOtp}
          onClose={closeOtp}
          handleLogout={handleLogout}
          resendCountdown={resendCountdown}
        />
      )}

      {isConfirmOpen && primaryEmail && (
        <MergingConfirmDialog
          primaryEmail={primaryEmail}
          isSubmitting={isSubmitting}
          onConfirm={submitMerging}
          onBack={() => setIsConfirmOpen(false)}
        />
      )}
    </BasicDialog>
  );
}